import { Component } from '@theme/component';
import { CollectionHistory, collectionCards } from '@aknvas/collection-history';
import { PaginationStatus } from '@aknvas/pagination-status';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo, getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  #generation = 0;
  #collectionHistory = new CollectionHistory(this);
  #loadingStatus = new PaginationStatus(this);

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#collectionHistory.initialize((first, last, active, anchorId) => this.#restoreRange(first, last, active, anchorId))
      .catch(this.#reportError)
      .finally(() => {
        if (!this.isConnected) return;
        this.#prefetchAdjacentPages();
        this.#observeViewMore();
      });

    scrollContainerMediaQuery.addEventListener('change', this.#refreshIntersectionRoot);

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    ++this.#generation;
    this.#loadingStatus.reset();
    this.#collectionHistory.disconnect();
    scrollContainerMediaQuery.removeEventListener('change', this.#refreshIntersectionRoot);
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #refreshIntersectionRoot = () => {
    this.infinityScrollObserver?.disconnect();
    this.infinityScrollObserver = undefined;
    this.#observeViewMore();
  };

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage().catch(this.#reportError);
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage().catch(this.#reportError);
              }
            }
          }
        },
        {
          root: getIntersectionRoot(),
          rootMargin: `${Math.max(600, Math.min(1600, window.innerHeight * 1.5))}px 0px`,
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);
    if (page && this.#shouldUsePage(page)) await this.#fetchSpecificPage(page.page, page.url);
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const generation = this.#generation;
    try {
      const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
      if (this.isConnected && generation === this.#generation) this.pages.set(pageNumber, pageContent);
    } catch (error) {
      if (this.isConnected && generation === this.#generation) throw error;
    }
  }

  async #renderNextPage() {
    const { grid } = this.refs;
    if (!grid) return false;
    const boundary = collectionCards(grid).at(-1);
    const generation = this.#generation;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);
    let finishLoading = () => {};
    try {
      if (!nextPageItemElements?.length) {
        finishLoading = this.#loadingStatus.begin(grid);
        await this.#fetchPage('next');
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements?.length) {
          if (this.isConnected && generation === this.#generation && this.refs.grid === grid) this.onPageLoadError?.();
          return false;
        }
      }
      if (!this.isConnected || generation !== this.#generation || this.refs.grid !== grid ||
          collectionCards(grid).at(-1) !== boundary) return false;

      grid.append(...nextPageItemElements);

      this.#aspectRatioHelper?.processNewElements();

      await yieldToMainThread();
      if (!this.isConnected || generation !== this.#generation) return false;

      this.#collectionHistory.updateUrl(nextPage.url);

      requestIdleCallback(() => {
        this.#fetchPage('next').catch(this.#reportError);
      });
      return true;
    } finally {
      finishLoading();
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;
    if (!grid) return false;
    const boundary = collectionCards(grid)[0];
    const generation = this.#generation;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements?.length) {
      await this.#fetchPage('previous');
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements?.length) return false;
    }
    if (!this.isConnected || generation !== this.#generation || this.refs.grid !== grid ||
        collectionCards(grid)[0] !== boundary) return false;

    // Store the current scroll position and height of the first element
    const currentScrollTop = getScrollTop();
    const firstElement = collectionCards(grid)[0];
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await yieldToMainThread();
    if (!this.isConnected || generation !== this.#generation) return false;

    this.#collectionHistory.updateUrl(previousPage.url);

    requestIdleCallback(() => {
      this.#fetchPage('previous').catch(this.#reportError);
    });
    return true;
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { grid } = this.refs;
    const isPrevious = type === 'previous';
    const targetCard = isPrevious ? collectionCards(grid)[0] : collectionCards(grid).at(-1);

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    const generation = ++this.#generation;
    this.#loadingStatus.reset();
    this.pages.clear();
    this.#collectionHistory.cancelRestore();
    this.#collectionHistory.forget();
    this.infinityScrollObserver?.disconnect();
    Promise.resolve(event.promise).then(() => {
      if (!this.isConnected || generation !== this.#generation) return;
      this.#prefetchAdjacentPages();
      this.#observeViewMore();
    }).catch(this.#reportError);
  };

  rememberPosition(cardId) {
    return this.#collectionHistory.remember(cardId);
  }

  #prefetchAdjacentPages() {
    this.#fetchPage('next').catch(this.#reportError);
    this.#fetchPage('previous').catch(this.#reportError);
  }

  async #restoreRange(first, last, active, anchorId) {
    while (active() && Number(collectionCards(this.refs.grid).at(-1)?.dataset.page) < last) {
      if (!await this.#renderNextPage()) return false;
    }
    while (active() && Number(collectionCards(this.refs.grid)[0]?.dataset.page) > first) {
      if (!await this.#renderPreviousPage()) return false;
    }
    // A changed viewport can put a remembered product into the unfinished tail.
    // Let responsive row preparation settle, then complete that row before scrolling.
    await yieldToMainThread();
    while (active() && this.ownerDocument.getElementById(anchorId)?.hasAttribute('data-aknvas-row-pending')) {
      if (!await this.#renderNextPage()) break;
    }
    return active() && Number(collectionCards(this.refs.grid)[0]?.dataset.page) <= first &&
      Number(collectionCards(this.refs.grid).at(-1)?.dataset.page) >= last;
  }

  #reportError = (error) => {
    if (error?.name !== 'AbortError') {
      this.onPageLoadError?.();
      console.warn('[collection-pagination] Could not load collection page:', error);
    }
  };
}
